import 'core-js/modules/web.dom-collections.iterator.js';
import * as React from 'react';
import classnames from 'classnames';
import styled from 'styled-components';
import { useField } from 'formik';
import { iconClassName, CheckmarkIcon } from '@thisisbud/bui-react-icons';
import { selectFromTheme, selectFromMap, selectFromProps, stylesFromMap } from '@thisisbud/sc-utils';
import { jsxs, jsx } from 'react/jsx-runtime';

let _ = t => t,
  _t,
  _div,
  _div2,
  _CheckmarkIcon;
const checkboxClassName = 'bui-checkbox';
const checkboxOutlineClassName = `${checkboxClassName}-outline`;
const checkboxVisualClassName = `${checkboxClassName}-visual`;

// eslint-disable-next-line @typescript-eslint/naming-convention
const StyledWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "StyledWrapper",
  componentId: "sc-1maaozx-0"
})(_t || (_t = _`
  display: inline-block;
  position: relative;
  width: ${0};
  height: ${0};
  padding: ${0};
  font-size: 1rem;
  box-sizing: border-box;

  input {
    position: absolute;
    display: block;
    appearance: none;
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    border: 0;
    opacity: 0;
    z-index: 3;
    cursor: pointer;
  }

  input:disabled {
    cursor: default;
  }

  .${0} {
    display: block;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
    box-sizing: content-box;
    border-width: ${0};
    border-style: solid;
    border-color: transparent;
    border-radius: ${0};
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
    pointer-events: none;
    z-index: 0;
    ${0}
  }

  input:focus ~ .${0} {
    opacity: 1;
  }

  .${0} {
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-style: solid;
    overflow: hidden;
    pointer-events: none;
    z-index: 1;
    transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
    background-color: ${0};
    border-color: ${0};
    border-radius: ${0};
    border-width: ${0};
  }

  input:disabled ~ .${0} {
    background-color: ${0};
    border-color: ${0};
  }

  input:not(:disabled):hover ~ .${0} {
    background-color: ${0};
    border-color: ${0};
  }

  input:checked ~ .${0} {
    ${0}
  }

  input:disabled:checked ~ .${0} {
    background-color: ${0};
    border-color: ${0};
  }

  input:not(:disabled):checked:hover ~ .${0} {
    ${0}
  }

  .${0} {
    position: absolute;
    display: block;
    pointer-events: none;
    z-index: 2;
    opacity: 0;
    transition: opacity 0.2s ease-in-out, color 0.2s ease-in-out;
    border-radius: ${0};
    border-width: ${0};
    color: ${0};
  }

  input:hover ~ .${0} {
    color: ${0};
  }

  input:disabled ~ .${0} {
    color: ${0};
  }

  input:checked ~ .${0} {
    opacity: 1;
  }
`), "1.5rem", "1.5rem", selectFromTheme('paddingCheckboxDefault'), checkboxOutlineClassName, selectFromTheme('outlineWidthCheckboxDefault'), selectFromTheme('borderRadiusCheckboxDefault'), function (props) {
  const {
    $error: error,
    theme
  } = props;
  const outlineColor = theme[error ? 'colorBorderCheckboxError' : 'colorTextPrimaryAccent'];
  const outlineWidth = theme['outlineWidthCheckboxDefault'];
  if (Boolean(outlineWidth) && Boolean(outlineColor)) {
    return `box-shadow: 0 0 0 ${outlineWidth} ${outlineColor};`;
  }
  return '';
}, checkboxOutlineClassName, checkboxVisualClassName, selectFromTheme('colorBackgroundCheckboxDefault'), selectFromMap(selectFromProps('$error'), {
  false: selectFromTheme('colorBorderCheckboxDefault'),
  true: selectFromTheme('colorBorderCheckboxError')
}), selectFromTheme('borderRadiusCheckboxDefault'), selectFromTheme('borderWidthCheckboxDefault'), checkboxVisualClassName, selectFromTheme('colorBackgroundCheckboxDisabled'), selectFromTheme('colorBorderCheckboxDisabled'), checkboxVisualClassName, selectFromTheme('colorBackgroundCheckboxHover'), selectFromMap(selectFromProps('$error'), {
  false: selectFromTheme('colorBorderCheckboxHover'),
  true: selectFromTheme('colorBorderCheckboxError')
}), checkboxVisualClassName, selectFromMap(selectFromProps('$error'), {
  false: stylesFromMap({
    'background-color': selectFromTheme('colorBackgroundCheckboxCheckedDefault'),
    // eslint-disable-line @typescript-eslint/naming-convention
    'border-color': selectFromTheme('colorBorderCheckboxCheckedDefault') // eslint-disable-line @typescript-eslint/naming-convention
  }),

  true: stylesFromMap({
    'background-color': selectFromTheme('colorBackgroundCheckboxCheckedError'),
    // eslint-disable-line @typescript-eslint/naming-convention
    'border-color': selectFromTheme('colorBorderCheckboxCheckedError') // eslint-disable-line @typescript-eslint/naming-convention
  })
}), checkboxVisualClassName, selectFromTheme('colorBackgroundCheckboxCheckedDisabled'), selectFromTheme('colorBorderCheckboxCheckedDisabled'), checkboxVisualClassName, selectFromMap(selectFromProps('$error'), {
  false: stylesFromMap({
    'background-color': selectFromTheme('colorBackgroundCheckboxCheckedHover'),
    // eslint-disable-line @typescript-eslint/naming-convention
    'border-color': selectFromTheme('colorBorderCheckboxCheckedHover') // eslint-disable-line @typescript-eslint/naming-convention
  }),

  true: stylesFromMap({
    'background-color': selectFromTheme('colorBackgroundCheckboxCheckedError'),
    // eslint-disable-line @typescript-eslint/naming-convention
    'border-color': selectFromTheme('colorBorderCheckboxCheckedError') // eslint-disable-line @typescript-eslint/naming-convention
  })
}), iconClassName, selectFromTheme('borderRadiusCheckboxDefault'), selectFromTheme('borderWidthCheckboxDefault'), selectFromMap(selectFromProps('$error'), {
  false: selectFromTheme('colorCheckboxCheckedDefault'),
  true: selectFromTheme('colorCheckboxCheckedError')
}), iconClassName, selectFromTheme('colorCheckboxCheckedHover'), iconClassName, selectFromTheme('colorCheckboxCheckedDisabled'), iconClassName);

/**
 * Renders a checkbox input field.
 *
 * *The `Checkbox` component needs to be a descendent of a `Form` component and its name must match one of its configured fields.*
 *
 * @param props - The component props
 * @public
 *
 * @example
 * ```javascript
 * import * as React from 'react'
 * import { Checkbox } from '@thisisbud/bui-react-checkbox'
 *
 * function Example() {
 *   return <Checkbox id='example' name='example'/>
 * }
 * ```
 */
function Checkbox(props) {
  // eslint-disable-line max-lines-per-function
  const {
    autoComplete,
    className,
    describedBy,
    disabled,
    error,
    id,
    name,
    onBlur,
    onChange,
    required
  } = props;
  const [field, meta, helpers] = useField({
    name: name,
    type: 'checkbox'
  });
  const computedError = Boolean(error !== null && error !== void 0 ? error : meta.error) && !disabled; // eslint-disable-line @typescript-eslint/strict-boolean-expressions

  // Allow using using onBlur event listeners
  const onBlurHandler = React.useCallback(function (e) {
    if (typeof onBlur === 'function') {
      onBlur(e);
    }
    field.onBlur(e);
  }, [field, onBlur]);

  // Allow using using onChange event listeners
  const onChangeHandler = React.useCallback(function (e) {
    if (typeof onChange === 'function') {
      onChange(e);
    }

    // Prevent value changes if the default is prevented
    if (!e.defaultPrevented) {
      if (typeof meta.error === 'string') {
        helpers.setError(undefined);
      }
      field.onChange(e);
    }
  }, [helpers, field, meta, onChange]);
  return /*#__PURE__*/jsxs(StyledWrapper, {
    $error: computedError,
    className: classnames(checkboxClassName, className),
    children: [/*#__PURE__*/jsx("input", {
      "aria-describedby": describedBy,
      autoComplete: autoComplete,
      checked: field.value,
      disabled: disabled,
      id: id,
      name: name,
      required: required,
      type: "checkbox",
      onBlur: onBlurHandler,
      onChange: onChangeHandler
    }), _div || (_div = /*#__PURE__*/jsx("div", {
      className: checkboxOutlineClassName
    })), _div2 || (_div2 = /*#__PURE__*/jsx("div", {
      className: checkboxVisualClassName
    })), _CheckmarkIcon || (_CheckmarkIcon = /*#__PURE__*/jsx(CheckmarkIcon, {}))]
  });
}
Checkbox.displayName = 'Checkbox';

export { Checkbox, checkboxClassName };
